/** Log message customer markers store customer ids. Legacy may still use `&G&name&G&`. */
import { unwrapLogWithLegacy, wrapLogCustomerMark } from './logFormat';

export type CustomerLike = {
  id?: string | number | null
  name?: string | null
  email?: string | null
  number?: string | null
  whatsapp?: string | null
  idDocUrl?: string | null
  /** Pre-extracted face (GCS images id) — preferred over MediaPipe cache. */
  facePhoto?: string | null
}

export type CustomerMaps = {
  byId: Map<string, CustomerLike>
  byName: Map<string, CustomerLike>
}

export const extractAllLogCustomerTokens = (message: string): string[] => (
  unwrapLogWithLegacy('customer', message || '')
);

export const buildCustomerMaps = (
  customers: CustomerLike[] | null | undefined,
): CustomerMaps => {
  const byId = new Map<string, CustomerLike>();
  const byName = new Map<string, CustomerLike>();
  (customers || []).forEach((c) => {
    if (c?.id != null && String(c.id) !== '' && String(c.id) !== '0') {
      byId.set(String(c.id), c);
    }
    if (c?.name) {
      byName.set(c.name.replace(/\s+/g, '').toLowerCase(), c);
      byName.set(c.name.toLowerCase(), c);
    }
  });
  return { byId, byName };
};

export const resolveCustomer = (
  token: string | null | undefined,
  maps: CustomerMaps,
): CustomerLike | null => {
  if (!token) return null;
  const byId = maps.byId.get(String(token));
  if (byId) return byId;
  const key = token.replace(/\s+/g, '').toLowerCase();
  return maps.byName.get(key) || maps.byName.get(token.toLowerCase()) || null;
};

export const REMOVED_CUSTOMER_LABEL = 'Unresolved';

/** Display name for a customer wrap (id → name; unresolved → Unresolved). */
export const resolveCustomerDisplayName = (
  token: string | null | undefined,
  maps: CustomerMaps,
  fallback = 'Guest',
): string => {
  if (!token) return fallback;
  const customer = resolveCustomer(token, maps);
  return customer?.name || REMOVED_CUSTOMER_LABEL;
};

/** Raw token to embed in new log messages (prefer customer id). */
export const logCustomerToken = (
  customerId: string | number | null | undefined,
  fallbackName?: string | null,
  fallback = 'Guest',
): string => {
  if (customerId != null && String(customerId) !== '' && String(customerId) !== '0') {
    return String(customerId);
  }
  if (fallbackName) return String(fallbackName);
  return fallback;
};

/** Full customer wrap for log messages. */
export const formatLogCustomer = (
  customerId: string | number | null | undefined,
  fallbackName?: string | null,
  fallback = 'Guest',
): string => wrapLogCustomerMark(logCustomerToken(customerId, fallbackName, fallback));
